"use client";

import { useRef, useState } from "react";

interface ImageProps {
  src?: string;
  alt?: string;
  maxWidth?: string;
  shouldLazyLoad?: boolean;
  objectPosition?: string;
  cover?: boolean;
  width?: string | number;
  height?: string | number;
  className?: string;
  imgClassName?: string;
  fillContainer?: boolean;
  imgStyle?: React.CSSProperties;
  sizes?: string;
  crop?: {
    top?: number;
    bottom?: number;
    left?: number;
    right?: number;
  } | null;
  hotspot?: {
    x: number;
    y: number;
  } | null;
  [key: string]: any;
}

function withQueryParams(
  url: string,
  params: Record<string, string | number | undefined>
) {
  if (!url) return "";

  try {
    const parsedUrl = new URL(url);
    for (const [key, value] of Object.entries(params)) {
      if (value === undefined || value === "") continue;
      parsedUrl.searchParams.set(key, String(value));
    }
    return parsedUrl.toString();
  } catch {
    return url;
  }
}

export default function Image({
  src = "",
  alt = "",
  maxWidth = "",
  shouldLazyLoad = true,
  objectPosition = "center",
  cover = true,
  width = "",
  height = "",
  className = "",
  imgClassName = "",
  fillContainer = false,
  imgStyle = {},
  sizes = "100vw",
  crop = null,
  hotspot = null,
  ...props
}: ImageProps) {
  // console.log("Image", src);
  const [loaded, setLoaded] = useState(true);
  const img = useRef(null);

  function onLoad() {
    setLoaded(true);
  }

  const proxyUrl = src.includes("https://images.ctfassets.net")
    ? src.replace("https://images.ctfassets.net", "https://illuminus-image-proxy.planet-site.workers.dev")
    : src;
  const isSanityCdnUrl = proxyUrl.includes("https://cdn.sanity.io");

  if (isSanityCdnUrl) {
    const desktopWidth = parseInt(maxWidth, 10) || 3400;

    return (
      <picture
        className={`${className} ${fillContainer ? "h-full w-full" : ""} ${
          loaded ? "opacity-100" : "opacity-0"
        }`}
        onLoad={onLoad}
        {...props}
      >
        <source
          type="image/webp"
          media="(max-width: 640px)"
          srcSet={withQueryParams(proxyUrl, { w: 640, fm: "webp" })}
        />
        <source
          type="image/webp"
          media="(min-width: 641px) and (max-width: 1024px)"
          srcSet={withQueryParams(proxyUrl, { w: 1024, fm: "webp" })}
        />
        <source
          type="image/webp"
          media="(min-width: 1025px)"
          srcSet={withQueryParams(proxyUrl, {
            w: desktopWidth,
            fm: "webp",
          })}
        />
        <img
          fetchPriority={shouldLazyLoad ? "low" : "high"}
          ref={img}
          width={width}
          height={height}
          src={withQueryParams(proxyUrl, { w: desktopWidth, fm: "jpg" })}
          className={`${objectPosition === "center" ? "object-center" : ``} ${
            cover ? "object-cover" : ""
          } ${imgClassName} ${fillContainer ? "h-full w-full" : ""}`}
          alt={alt}
          loading={shouldLazyLoad ? "lazy" : "eager"}
          decoding="async"
          sizes={sizes}
          onLoad={onLoad}
          style={{
            ...imgStyle,
            objectPosition: objectPosition !== "center" ? objectPosition : "",
          }}
        />
      </picture>
    );
  }

  return (
    <picture
      className={`${className} ${fillContainer ? "h-full w-full" : ""} ${
        loaded ? "opacity-100" : "opacity-0"
      }`}
      onLoad={onLoad}
      {...props}
    >
      <source
        type="image/avif"
        media="(max-width: 500px)"
        srcSet={withQueryParams(proxyUrl, { fm: "avif", w: 1000 })}
      />
      <source
        type="image/avif"
        media="(max-width: 1200px)"
        srcSet={withQueryParams(proxyUrl, { fm: "avif", w: 1200 })}
      />
      <source
        type="image/avif"
        media="(min-width: 1201px)"
        srcSet={withQueryParams(proxyUrl, {
          fm: "avif",
          w: maxWidth || undefined,
        })}
      />
      <source
        type="image/webp"
        media="(max-width: 500px)"
        srcSet={withQueryParams(proxyUrl, { fm: "webp", w: 1000 })}
      />
      <source
        type="image/webp"
        media="(max-width: 1200px)"
        srcSet={withQueryParams(proxyUrl, { fm: "webp", w: 1200 })}
      />
      <source
        type="image/webp"
        media="(min-width: 1201px)"
        srcSet={withQueryParams(proxyUrl, {
          fm: "webp",
          w: maxWidth || undefined,
        })}
      />
      <source
        type="image/jpeg"
        media="(max-width: 500px)"
        srcSet={withQueryParams(proxyUrl, { fm: "jpg", w: 1000 })}
      />
      <source
        type="image/jpeg"
        media="(max-width: 1200px)"
        srcSet={withQueryParams(proxyUrl, { fm: "jpg", w: 1200 })}
      />
      <source
        type="image/jpeg"
        media="(min-width: 1201px)"
        srcSet={withQueryParams(proxyUrl, {
          fm: "jpg",
          w: maxWidth || undefined,
        })}
      />
      <img
        fetchPriority={shouldLazyLoad ? "low" : "high"}
        ref={img}
        width={width}
        height={height}
        src={withQueryParams(proxyUrl, { w: maxWidth || undefined })}
        className={`${objectPosition === "center" ? "object-center" : ``} ${
          cover ? "object-cover" : ""
        } ${imgClassName} ${fillContainer ? "h-full w-full" : ""}`}
        alt={alt}
        loading={shouldLazyLoad ? "lazy" : "eager"}
        decoding="async"
        sizes={sizes}
        onLoad={onLoad}
        style={{
          ...imgStyle,
          objectPosition: objectPosition !== "center" ? objectPosition : "",
        }}
      />
    </picture>
  );
}
